import React from 'react';
import { Box, SxProps, Theme, useMediaQuery, useTheme } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import { ScrollRevealVariant } from './scrollMotion';
import { useScrollReveal } from './useScrollReveal';

const MotionBox = motion.create(Box);

interface ScrollRevealProps {
  children: React.ReactNode;
  sx?: SxProps<Theme>;
  variant?: ScrollRevealVariant;
  fadeOut?: boolean;
}

const ScrollReveal: React.FC<ScrollRevealProps> = ({
  children,
  sx,
  variant = 'content',
  fadeOut = true,
}) => {
  const reduceMotion = useReducedMotion();
  const { ref, motion: scrollMotion } = useScrollReveal(variant, fadeOut);

  if (reduceMotion) {
    return (
      <Box ref={ref} sx={sx}>
        {children}
      </Box>
    );
  }

  return (
    <MotionBox
      ref={ref}
      style={{
        opacity: scrollMotion.opacity,
        y: scrollMotion.y,
        ...(scrollMotion.filter ? { filter: scrollMotion.filter } : {}),
      }}
      sx={{
        willChange: scrollMotion.filter
          ? 'transform, opacity, filter'
          : 'transform, opacity',
        ...sx,
      }}
    >
      {children}
    </MotionBox>
  );
};

/** Große Headlines — blur on scroll (desktop), simple fade (mobile) */
export const ScrollBlurHeadline: React.FC<Omit<ScrollRevealProps, 'variant'>> = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  return <ScrollReveal {...props} variant={isMobile ? 'content' : 'headline'} fadeOut={!isMobile} />;
};

export default ScrollReveal;
